import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { UsersMenu } from '../UsersMenu';
import { Helmet } from 'react-helmet';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import { v4 as uuidv4 } from 'uuid';
import { FiEdit3 } from 'react-icons/fi';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';
import { StoreSettings } from './storeTypes';
import { InputComponent } from '../../../ReusableComponents/Input/InputComponent';
import { fetchAllStoresInfo } from '../../../fetch/fetchStore';
import { TableContainer } from '@material-ui/core';
import TablePagination from '@material-ui/core/TablePagination';
import { capitalize } from '../../../utils/strings';
import { isEqual } from 'lodash';
import './StoreSettings.scss';
import { FaSortAmountDown, FaSortAmountDownAlt } from 'react-icons/fa';
import { Region } from 'types/orderTypes';
import { SortOrdering } from 'types/tableTypes';

enum TableHeaders {
  storeName = 'Store name',
  region = 'Region',
  enabled = 'Enabled',
  consolidated = 'Consolidation',
}

interface SortingObject {
  sortKey: string;
  sortOrdering: SortOrdering;
}

type TableHeadersTypes = keyof typeof TableHeaders;

function sortMethodForTableColumn(
  a: StoreSettings,
  b: StoreSettings,
  sortByFromStoresCapacity: string
) {
  if (sortByFromStoresCapacity === 'enabled') {
    if (a.enabled === b.enabled) {
      return 0;
    }
    return a.enabled ? 1 : -1;
  } else if (sortByFromStoresCapacity === 'region') {
    return a.deliveryRegions.length - b.deliveryRegions.length;
  }
  return a.name.localeCompare(b.name);
}

const AllStoreSettings = () => {
  const [storeSettings, setStoreSettings] = useState<StoreSettings[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(24);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [sortObject, setSortObject] = useState<SortingObject>({
    sortKey: 'storeName',
    sortOrdering: 'asc',
  });
  const [filteredSortedStores, setFilteredSortedStores] = useState<
    StoreSettings[] | undefined
  >(undefined);

  const sortingByKeys = (
    sortingObject: SortingObject,
    storesToSort: StoreSettings[]
  ): StoreSettings[] => {
    const sortedStores = [...storesToSort].sort((a, b) =>
      sortMethodForTableColumn(a, b, sortingObject.sortKey)
    );

    return sortingObject.sortOrdering === 'desc'
      ? sortedStores.reverse()
      : sortedStores;
  };

  useEffect(() => {
    fetchAllStoresInfo()
      .then(fetchedStoreSettings => {
        const tempSortObject: SortingObject = {
          sortKey: 'storeName',
          sortOrdering: 'asc',
        };
        const sortedStore = sortingByKeys(tempSortObject, fetchedStoreSettings);
        setStoreSettings(sortedStore);
      })
      .catch(() => {
        console.log('Could not fetch stores');
      });
  }, []);

  const handleChangePage = (_: any, newPage: number): void => setPage(newPage);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const filterAction = (
    inputValue: string,
    _sortObject: SortingObject = sortObject
  ): void => {
    let newValue: StoreSettings[] = storeSettings;
    if (inputValue) {
      inputValue = inputValue.toLowerCase();
      newValue = newValue.filter(({ name }) =>
        name.toLowerCase().includes(inputValue)
      );
    }
    const sortedValue = sortingByKeys(_sortObject, newValue);

    if (!isEqual(filteredSortedStores, sortedValue)) {
      setFilteredSortedStores(sortedValue);
    }
  };

  const onSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setSearchInputValue(value);
    filterAction(value);
  };

  const setActiveSorting = (event: MouseEvent<HTMLDivElement>): void => {
    const { key } = event.currentTarget.dataset;
    let sortOrdering: SortOrdering = 'desc';

    if (sortObject.sortKey === key) {
      sortOrdering = sortObject.sortOrdering === 'asc' ? 'desc' : 'asc';
    }

    const newSortObject: SortingObject = {
      sortKey: key,
      sortOrdering,
    };

    filterAction(searchInputValue, newSortObject);
    setSortObject(newSortObject);
  };

  const RegionCell = ({ regions }: { regions: string[] }): JSX.Element => {
    // Text if cell has all the regions
    if (regions.length === Object.keys(Region).length)
      return <span>All regions</span>;

    // Sort regions alphabetically
    regions.sort();

    const renderRegions = () =>
      regions.map(region => {
        const label = capitalize(region.toLowerCase());
        return (
          <span key={region} className="store-regions__item">
            {label}
          </span>
        );
      });

    return <div className="store-regions">{renderRegions()}</div>;
  };

  return (
    <>
      <Helmet>
        <title>Users - Store settings</title>
      </Helmet>
      <UsersMenu />
      <div className="store-settings__store-container">
        <section className="store-settings-list">
          <div className="store-settings-header">
            <h1>Store overview</h1>
            <InputComponent
              className="store-settings.store-search-input"
              id="store-search"
              name="store-search"
              label="Search store"
              value={searchInputValue}
              size="medium"
              onChange={onSearchInputChange}
            />
          </div>
          <TableContainer
            className="store-settings__store-table"
            component="div"
          >
            <Table>
              <TableHead>
                <TableRow>
                  {Object.keys(TableHeaders).map((key: string) => (
                    <TableCell
                      data-key={key}
                      align={key !== 'storeName' ? 'center' : 'left'}
                      key={key}
                      onClick={setActiveSorting}
                    >
                      {TableHeaders[key as TableHeadersTypes]}
                      {sortObject.sortKey === key ? (
                        sortObject.sortOrdering === 'asc' ? (
                          <FaSortAmountDownAlt data-key={key} />
                        ) : (
                          <FaSortAmountDown data-key={key} />
                        )
                      ) : null}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(filteredSortedStores || storeSettings)
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((store: StoreSettings, ix: number) => {
                    return (
                      <TableRow key={uuidv4()}>
                        <TableCell>{store.name}</TableCell>
                        <TableCell align="center">
                          <RegionCell regions={store.deliveryRegions} />
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={`capacity-info__dot ${
                              store.enabled ? 'green' : 'red'
                            }`}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={`capacity-info__dot ${
                              store.consolidationSupport === 'NONE'
                                ? 'red'
                                : 'green'
                            }`}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <a
                            href={`/users/storesettings/editstore/${store.accountId}`}
                          >
                            <ButtonComponent
                              startIcon={<FiEdit3 />}
                              text="Edit"
                              variant="text"
                              value={store.name}
                            />
                          </a>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[8, 16, 24]}
            count={(filteredSortedStores || storeSettings)?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </section>
      </div>
    </>
  );
};

export default AllStoreSettings;
